import { useState, useEffect, useContext, useRef, useMemo } from "react";
import Modal from 'react-modal';
import cx from "classnames";

import ChartJS from 'chart.js/auto';
import { Chart } from 'react-chartjs-2';

import { fetchChartRender } from "./api";

import styles from "./chart.module.css";

import { placeholderDatasetPlugin } from "./placeholder-plugin";
import { doughnutTextPlugin } from "./doughnut-text-plugin";

ChartJS.defaults.font.size = 16; // Set global font size
ChartJS.defaults.font.family = 'Arial'; // Set global font family
//ChartJS.defaults.font.weight = '100'; // Set global font weight


const useRendering = (start, end, activeAccounts, config, spaceScopedFetch) => {
    const [renderingConfig, setRenderingConfig] = useState(false);
    const [renderedChart, setRenderedChart] = useState(null);
    const configRef = useRef();

    const renderChart = async () => {
        configRef.current = config;
        setRenderingConfig(config);
        const newRenderedChart = await fetchChartRender(start, end, activeAccounts, config, spaceScopedFetch);
        setRenderingConfig(null);
        if (configRef.current === config) {
            setRenderedChart(newRenderedChart);
        }
    };

    useEffect(() => {
        renderChart();
    }, [start, end, activeAccounts, config]);

    return [ renderedChart, renderingConfig === config ];
};

const FigureChart = ({ start, end, activeAccounts, config, spaceScopedFetch, className }) => {
    const [renderedChart, renderingConfig] = useRendering(
        start, end, activeAccounts, config, spaceScopedFetch
    );

    if (!renderedChart) {
        return <p>Loading...</p>;
    }

    return (
        <div className={cx(
            className,
            styles.chart, { [styles.rendering]: renderingConfig === config }
        )}>
            { renderedChart.data.value }
        </div>
    );
};

const ChartJSChart = ({ start, end, activeAccounts, config, spaceScopedFetch }) => {
    const [renderedChart, isRendering] = useRendering(
        start, end, activeAccounts, config, spaceScopedFetch
    );
    const chartRef = useRef();

    const generateBackgroundColor = possibleGradient => {
        const isGradient = (
            Array.isArray(possibleGradient)
            && possibleGradient.length > 0
            && possibleGradient[0] === "gradient"
        );
        if (isGradient) {
            const gradientSize = 40;
            const gradientCanvas = document.createElement("canvas");
            gradientCanvas.width = gradientSize;
            gradientCanvas.height = gradientSize;

            const gradientContext = gradientCanvas.getContext("2d");
            const gradient = gradientContext.createLinearGradient(0, 0, gradientSize, gradientSize);
            possibleGradient = possibleGradient.toSpliced(0, 1);

            gradient.addColorStop(0, possibleGradient[0]);
            possibleGradient.forEach((c, i) => {
                const fromColor = possibleGradient.at(i);
                const toColor = possibleGradient.at((i + 1) % possibleGradient.length);
                const transitions = [
                    (2 * i + 1) / (possibleGradient.length * 4),
                    (2 * i + 1) / (possibleGradient.length * 4) + 0.5
                ];
                transitions.forEach(transition => {
                    gradient.addColorStop(transition, fromColor);
                    gradient.addColorStop(transition, toColor);
                });
            });
            gradient.addColorStop(1, possibleGradient[0]);

            gradientContext.fillStyle = gradient;
            gradientContext.fillRect(0, 0, gradientSize, gradientSize);
            return gradientContext.createPattern(gradientCanvas, "repeat");
        }
        if (Array.isArray(possibleGradient)) {
            return possibleGradient.map(generateBackgroundColor);
        }
        return possibleGradient;
    };

    const data = useMemo(() => {
        if (!renderedChart) {
            return null;
        }
        return {
            ...renderedChart.data,
            datasets: renderedChart.data.datasets.map(dataset => ({
                ...dataset,
                backgroundColor: generateBackgroundColor(dataset.backgroundColor)
            }))
        };
    }, [renderedChart]);

    if (!renderedChart) {
        return <p>Loading...</p>;
    }

    return (
        <div className={cx(
            styles.chart, { [styles.rendering]: isRendering }
        )}>
            <Chart
                type={renderedChart.type}
                data={data}
                ref={chartRef}
                options={renderedChart.options}
                plugins={[placeholderDatasetPlugin, doughnutTextPlugin]}
            />
        </div>
    );
};


export default ({ config, ...restProps }) => {
    if(config.type === "figure") {
        return (
            <FigureChart
                config={config}
                {...restProps}
            />
        )
    } else {
        return (
            <ChartJSChart
                config={config}
                {...restProps}
            />
        );
    }
};
